/**
 * Funds Module
 * Provides data access interfaces for funds
 */

import { Client, ClientOptions } from '../../utils/client';
import { convertKlinePeriod } from '../../utils';
import { APIResponse } from '../../types'
import { TickData, TickDataMap } from '../../types/tick';
import { QuoteData, QuoteDataMap } from '../../types/quote';
import { DepthData, DepthDataMap } from '../../types/depth';
import { KlineData, KlineDataMap, GetKlineOptions, GetKlinesOptions } from '../../types/kline';
import { CreateSocketOptions } from '../../types/socket';
import SocketClient from '../../utils/SocketClient';

/**
 * Funds SDK Client
 * Specialized for funds data access
 * 
 */
export class FundClient extends Client {
    constructor(token: string, options?: ClientOptions) {
        super(token, options);
    }
    /**
     * Get Funds Real-Time Trade Snapshot
     * @description Query latest trade real-time market data for funds based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, HK etc.), see {@link https://docs.itick.org/rest-api/fund/fund-tick Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., SPY, QQQ etc.)
     * @returns Promise wrapped standard API response, data is real-time market data {@link TickData}
     * @example
     * ```typescript
     * // Get real-time trade market data for SPY fund
     * getTick({ region: 'US', code: 'SPY' }).then(response => {
     *     console.log('Real-time market:', response.data);
     *   }).catch(err => {
     *     console.error('Failed to get market data:', err);
     *   });
     * ```
     */
    async getTick(params: { region: string, code: string }): Promise<APIResponse<TickData>> {
        const { region, code } = params;
        return this._get('/fund/tick', { region, code });
    }

    /**
     * Get Funds Latest Quote
     * @description Query latest quote real-time market data for funds based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, HK etc.), see {@link https://docs.itick.org/rest-api/fund/fund-quote Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., SPY, QQQ etc.)
     * @returns Promise wrapped API response, data is latest quote data {@link QuoteData}
     * @example
     * ```typescript
     * // Get latest quote for SPY fund
     * getQuote({ region: 'US', code: 'SPY' }).then(response => {
     *    console.log('Latest quote:', response.data);
     * }).catch(err => {
     *   console.error('Failed to get quote:', err);
     * });
     * ```
     */
    async getQuote(params: { region: string, code: string }): Promise<APIResponse<QuoteData>> {
        const { region, code } = params;
        return this._get('/fund/quote', { region, code });
    }

    /**
     * Get Funds Latest Order Book
     * @description Query latest order book data for funds based on market code and symbol code
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, HK etc.), see {@link https://docs.itick.org/rest-api/fund/fund-depth Official Documentation} for complete supported list
     * @param params.code - Symbol code (e.g., SPY, QQQ etc.)
     * @returns Promise wrapped API response, data is latest order book data {@link DepthData}
     * @example
     * ```typescript
     * // Get latest order book for SPY fund
     * getDepth({ region: 'US', code: 'SPY' }).then(response => {
     *   console.log('Latest order book:', response.data);
     * }).catch(err => {
     *  console.error('Failed to get order book:', err);
     * });
     * ```
     */
    async getDepth(params: { region: string, code: string }): Promise<APIResponse<DepthData>> {
        const { region, code } = params;
        return this._get('/fund/depth', { region, code });
    }

    /**
     * Get Funds K-Line Data
     * @description Query funds K-line data based on market code, symbol code, K-line period, end time and number of records to return
     * @param options - Query parameters object see {@link GetKlineOptions}
     * @param options.region - Market code (e.g., US, HK etc.), see {@link https://docs.itick.org/rest-api/fund/fund-kline Official Documentation} for complete supported list
     * @param options.code - Symbol code (e.g., SPY, QQQ etc.)
     * @returns Promise wrapped API response, data is K-line data array {@link KlineData}
     * @example
     * ```typescript
     * // Get 5-minute K-line data for SPY fund
     * getKline({ region: 'US', code: 'SPY', interval: '5m', limit: 100 }).then(response => {
     *   console.log('K-line data:', response.data);
     * }).catch(err => {
     *  console.error('Failed to get K-line data:', err);
     * });
     * ```
     */
    async getKline(options: GetKlineOptions): Promise<APIResponse<KlineData[]>> {
        const { region, code, kType, interval, limit, et } = options;
        const params: Record<string, any> = { region, code, kType: convertKlinePeriod(interval ?? kType), limit };
        if (et) params.et = et;
        return this._get('/fund/kline', params);
    }

    /**
     * Get Multiple Funds Latest Trades
     * @description Query latest trade real-time market data for multiple funds based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, HK etc.), see {@link https://docs.itick.org/rest-api/fund/fund-ticks Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., SPY, QQQ etc.)
     * @returns Promise wrapped API response, data is multiple funds latest trade data object {@link TickDataMap}
     * @example
     * ```typescript
     * // Get latest trade market data for SPY and QQQ funds
     * getTicks({ region: 'US', codes: ['SPY', 'QQQ'] }).then(response => {
     *  console.log('Latest trades:', response.data);
     * }).catch(err => {
     * console.error('Failed to get trade market data:', err);
     * });
     * ```
     */
    async getTicks(params: { region: string, codes: string[] | string }): Promise<APIResponse<TickDataMap>> {
        const { region, codes } = params;
        return this._get('/fund/ticks', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Funds Latest Quotes
     * @description Query latest quote real-time market data for multiple funds based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, HK etc.), see {@link https://docs.itick.org/rest-api/fund/fund-quotes Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., SPY, QQQ etc.)
     * @returns Promise wrapped API response, data is multiple funds latest quote data object {@link QuoteDataMap}
     * @example
     * ```typescript
     * // Get latest quotes for SPY and QQQ funds
     * getQuotes({ region: 'US', codes: ['SPY', 'QQQ'] }).then(response => {
     * console.log('Latest quotes:', response.data);
     * }).catch(err => {
     * console.error('Failed to get quotes:', err);
     * });
     * ```
     */
    async getQuotes(params: { region: string, codes: string[] | string }): Promise<APIResponse<QuoteDataMap>> {
        const { region, codes } = params;
        return this._get('/fund/quotes', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Funds Latest Order Books
     * @description Query latest order book real-time market data for multiple funds based on market code and symbol code list
     * @param params - Query parameters object
     * @param params.region - Market code (e.g., US, HK etc.), see {@link https://docs.itick.org/rest-api/fund/fund-depths Official Documentation} for complete supported list
     * @param params.codes - Symbol code list (e.g., SPY, QQQ etc.)
     * @returns Promise wrapped API response, data is multiple funds latest order book data object {@link DepthDataMap}
     * @example
     * ```typescript
     * // Get latest order books for SPY and QQQ funds
     * getDepths({ region: 'US', codes: ['SPY', 'QQQ'] }).then(response => {
     * console.log('Latest order books:', response.data);
     * }).catch(err => {
     * console.error('Failed to get order books:', err);
     * });
     * ```
     */
    async getDepths(params: { region: string, codes: string[] | string }): Promise<APIResponse<DepthDataMap>> {
        const { region, codes } = params;
        return this._get('/fund/depths', { region, codes: Array.isArray(codes) ? codes.join(',') : codes });
    }

    /**
     * Get Multiple Funds K-Line Data
     * @description Query K-line data for multiple funds based on market code, symbol code list, K-line period, end time and number of records to return
     * @param options - Query parameters object
     * @param options.region - Market code (e.g., US, HK etc.), see {@link https://docs.itick.org/rest-api/fund/fund-klines Official Documentation} for complete supported list
     * @param options.codes - Symbol code list (e.g., SPY, QQQ etc.)
     * @returns Promise wrapped API response, data is multiple funds K-line data object {@link KlineDataMap}
     * @example
     * ```typescript
     * // Get 5-minute K-line data for SPY and QQQ funds
     * getKlines({ region: 'US', codes: ['SPY', 'QQQ'], interval: '5m', limit: 100 }).then(response => {
     *  console.log('K-line data:', response.data);
     * }).catch(err => {
     * console.error('Failed to get K-line data:', err);
     * });
     * ```
     */
    async getKlines(options: GetKlinesOptions): Promise<APIResponse<KlineDataMap>> {
        const { region, codes, kType, interval, limit, et } = options;
        const params: Record<string, any> = { region, codes: Array.isArray(codes) ? codes.join(',') : codes, kType: convertKlinePeriod(interval ?? kType), limit };
        if (et) params.et = et;
        return this._get('/fund/klines', params);
    }
    /**
     * Create WebSocket Connection
     * @description Creates a WebSocket connection and returns a SocketClient object for managing connection and subscribing to real-time data. Subscription data can be specified through options parameter.
     * Automatically handles connection retry and heartbeat mechanism to ensure connection stability and reliability.
     * @param options - Optional options object for creating WebSocket connection {@link CreateSocketOptions}
     * @returns SocketClient object for managing WebSocket connection
     * @example
     * ```typescript
     * // Create a WebSocket connection and subscribe to 5-minute K-line and real-time quote data for SPY and QQQ funds
     * const client = new FundClient(token);
     * const socket = client.createSocket({
     *  subscribeData: {
     *    codes: ['SPY$US', 'QQQ$US'],
     *    types: ['kline@5m', 'quote']
     *  }
     * });
     * socket.onSocketMessage(data => {  
     *  console.log('Received WebSocket message:', data);
     * });
     * ```
     * @see Official Documentation https://docs.itick.org/websocket/fund
     */
    createSocket(options?: CreateSocketOptions): SocketClient {
        return this._createSocket('/fund', options);
    }
}
